import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_DIGITS_REGEX = /^\d+$/;

export default function ShareQuoteModal({
    isOpen,
    toggle,
    onSubmit,
    isSubmitting = false,
}) {
    const [formData, setFormData] = useState({ email: "", mobile: "" });
    const [touched, setTouched] = useState({ email: false, mobile: false });
    const [formError, setFormError] = useState("");

    const validationErrors = useMemo(() => {
        const errors = {};
        const email = (formData.email || "").trim();
        const mobile = (formData.mobile || "").trim();

        if (email && !EMAIL_REGEX.test(email)) {
            errors.email = "Please enter a valid email";
        }

        if (mobile && !MOBILE_DIGITS_REGEX.test(mobile)) {
            errors.mobile = "Mobile must contain digits only";
        } else if (mobile && (mobile.length < 10 || mobile.length > 15)) {
            errors.mobile = "Mobile must be between 10 and 15 digits";
        }

        return errors;
    }, [formData]);

    const isFormValid = Object.keys(validationErrors).length === 0;

    useEffect(() => {
        if (!isOpen) {
            setFormData({ email: "", mobile: "" });
            setTouched({ email: false, mobile: false });
            setFormError("");
        }
    }, [isOpen]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === "mobile" ? value.replace(/\s+/g, "") : value,
        }));
    };

    const handleBlur = (event) => {
        const { name } = event.target;
        setTouched((prev) => ({ ...prev, [name]: true }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        setTouched({ email: true, mobile: true });
        const email = formData.email.trim();
        const mobile = formData.mobile.trim();

        if (!email && !mobile) {
            setFormError("Please enter Email or Mobile.");
            return;
        }

        if (!isFormValid || typeof onSubmit !== "function") {
            return;
        }

        setFormError("");

        onSubmit({
            email,
            mobile,
        });
    };

    const handleCancel = () => {
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} centered>
            <ModalHeader toggle={handleCancel}>Share Quote</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <p className="text-muted mb-4" style={{ fontSize: "16px" }}>
                        Enter the details below to forward this quote.
                    </p>
                    <FormGroup className="mb-3">
                        <Input
                            id="share-quote-email"
                            name="email"
                            type="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.email && !!validationErrors.email}
                            className="form-control-lg"
                            style={{ minHeight: "56px", borderRadius: "8px" }}
                        />
                        {touched.email && validationErrors.email ? (
                            <FormFeedback>{validationErrors.email}</FormFeedback>
                        ) : null}
                    </FormGroup>

                    <FormGroup className="mb-0">
                        <Input
                            id="share-quote-mobile"
                            name="mobile"
                            type="tel"
                            placeholder="Mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.mobile && !!validationErrors.mobile}
                            maxLength={15}
                            className="form-control-lg"
                            style={{ minHeight: "56px", borderRadius: "8px" }}
                        />
                        {touched.mobile && validationErrors.mobile ? (
                            <FormFeedback>{validationErrors.mobile}</FormFeedback>
                        ) : null}
                    </FormGroup>
                    {formError ? (
                        <div className="text-danger mt-2" style={{ fontSize: "0.875rem" }}>
                            {formError}
                        </div>
                    ) : null}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Share Now"}
                </Button>
                <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
}
